import { useCallback } from 'react';
import { useFormContext, UseFormReturn } from 'react-hook-form';
import { LabelValueAcceptedBy, LabelValueSource } from 'graphql/types';
import { useLabelsFormFieldContext } from 'modules/timesheet-labels';

import { useFeatureFlag } from 'modules/common/hooks/useFeatureFlag';
import {
  PCPTVariantType,
  DependantLabelData,
  DependantLabels
} from '../components/schema';

const setLabelValue = (
  label: DependantLabelData,
  setValue: UseFormReturn['setValue'],
  formField: string,
  isLabelLevelAcceptedByEnabled: boolean,
  isTimeSignalFederatedSuggestionsEnabled: boolean
) => {
  setValue(
    `${formField}.${label.index}.label`,
    isLabelLevelAcceptedByEnabled
      ? {
          ...label.label,
          ...(isTimeSignalFederatedSuggestionsEnabled && {
            value: label.label.suggestedValues?.[0]
          }),
          acceptedBy: LabelValueAcceptedBy.User,
          valueSource: label.label.valueSource ?? LabelValueSource.User
        }
      : {
          ...label.label,
          ...(isTimeSignalFederatedSuggestionsEnabled && {
            value: label.label.suggestedValues?.[0]
          }),
          valueSource: LabelValueSource.User
        },
    { shouldValidate: true, shouldDirty: true }
  );
};
const getProgramClientValue = (
  client: DependantLabelData | undefined,
  program: DependantLabelData | undefined
) => {
  if (client) {
    return client;
  } else if (program) {
    return program;
  } else return;
};

export const useAcceptCptSuggestion = (
  dependentLabels: DependantLabels,
  labelType: PCPTVariantType
): (() => void) => {
  const { setValue } = useFormContext();
  const {
    timesheetLabels: timesheetLabelsFormField
  } = useLabelsFormFieldContext();
  const { client, project, task, program } = dependentLabels;
  const programClientValue = getProgramClientValue(client, program);
  const isLabelLevelAcceptedByEnabled = useFeatureFlag(
    'isLabelLevelAcceptedByEnabled',
    false
  );
  const isTimeSignalFederatedSuggestionsEnabled = useFeatureFlag(
    'isTimeSignalFederatedSuggestionsEnabled',
    false
  );
  return useCallback(() => {
    switch (labelType) {
      case 'task':
        setLabelValue(
          task,
          setValue,
          timesheetLabelsFormField,
          isLabelLevelAcceptedByEnabled,
          isTimeSignalFederatedSuggestionsEnabled
        );
        setLabelValue(
          project,
          setValue,
          timesheetLabelsFormField,
          isLabelLevelAcceptedByEnabled,
          isTimeSignalFederatedSuggestionsEnabled
        );
        programClientValue &&
          setLabelValue(
            programClientValue,
            setValue,
            timesheetLabelsFormField,
            isLabelLevelAcceptedByEnabled,
            isTimeSignalFederatedSuggestionsEnabled
          );
        break;
      case 'project':
        setLabelValue(
          project,
          setValue,
          timesheetLabelsFormField,
          isLabelLevelAcceptedByEnabled,
          isTimeSignalFederatedSuggestionsEnabled
        );
        programClientValue &&
          setLabelValue(
            programClientValue,
            setValue,
            timesheetLabelsFormField,
            isLabelLevelAcceptedByEnabled,
            isTimeSignalFederatedSuggestionsEnabled
          );
        break;
      case 'client':
        client &&
          setLabelValue(
            client,
            setValue,
            timesheetLabelsFormField,
            isLabelLevelAcceptedByEnabled,
            isTimeSignalFederatedSuggestionsEnabled
          );
        break;
      case 'program':
        program &&
          setLabelValue(
            program,
            setValue,
            timesheetLabelsFormField,
            isLabelLevelAcceptedByEnabled,
            isTimeSignalFederatedSuggestionsEnabled
          );
    }
  }, [
    labelType,
    task,
    setValue,
    timesheetLabelsFormField,
    isLabelLevelAcceptedByEnabled,
    isTimeSignalFederatedSuggestionsEnabled,
    project,
    programClientValue,
    client,
    program
  ]);
};
